// inspiration: https://codepen.io/n0rmand0/pen/qVrMeG
import { darken, useTheme } from '@mui/material/styles';
import classNames from 'classnames';
import { useMemo } from 'react';

import TokenLogo from './TokenLogo';

import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const rootCss = ({ palette, typography }) => ({
  fontSize: 40,
  position: 'relative',
  width: `${DIAMETER}em`,
  height: `${DIAMETER}em`,
  transformStyle: 'preserve-3d',
  animation: `rotate3d ${TURN_TIME}s linear infinite`,
  transition: 'all .3s',
  '.WT-coinFace': {
    backgroundSize: 'cover',
    position: 'absolute',
    width: `${DIAMETER}em`,
    height: `${DIAMETER}em`,
    borderRadius: '50%',
    overflow: 'hidden',

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '0.08em solid rgba(0,0,0,0.2)',
    '&:after': {
      // shine effect
      content: "''",
      position: 'absolute',
      left: `-${DIAMETER / 2}em`,
      bottom: '100%',
      display: 'block',
      height: `${DIAMETER / 1.5}em`,
      width: `${DIAMETER * 2}em`,
      background: palette.common.white,
      opacity: 0.3,
      animation: `shine linear ${TURN_TIME / 2}s infinite`,
    },
  },
  '.WT-coinFace_front': {
    transform: `translateZ(${THICKNESS / 2}em)`,
  },
  '.WT-coinFace_back': {
    transform: `translateZ(-${THICKNESS / 2}em) rotateY(180deg)`,
  },
  '.WT-coinFaceIcon': {
    fontSize: '0.45em',
    opacity: 0.2,
    color: palette.common.black,
    fontWeight: typography.fontWeightBlack,
  },
  '.WT-coinEdge': {
    transformStyle: 'preserve-3d',
    '& > *': {
      position: 'absolute',
      height: `${Math.PI / EDGES}em`,
      width: `${THICKNESS}em`,
      background: palette.yellow.main,
    },
  },

  '.WT-coinShadow': {
    position: 'absolute',
    width: `${DIAMETER}em`,
    height: `${THICKNESS}em`,
    borderRadius: '50%',
    background: '#000',
    boxShadow: `0 0 ${THICKNESS * 5}em ${THICKNESS * 5}em #000`,
    opacity: '0.125',
    transform: `rotateX(90deg) translateZ(-${DIAMETER * 1.1}em) scale(.5)`,
  },

  '@keyframes rotate3d': {
    '0%': { transform: 'perspective(1000px) rotateY(0deg)' },
    '100%': { transform: 'perspective(1000px) rotateY(360deg)' },
  },
  '@keyframes shine': {
    '0%, 15%': {
      transform: 'translateY(2em) rotate(-40deg)',
    },
    '50%': {
      transform: 'translateY(-1em) rotate(-40deg)',
    },
  },
});

const EDGES = 80;
const TURN_TIME = 6;
const DIAMETER = 1;
const THICKNESS = DIAMETER / 12; // DIAMETER-THCKNESS RATIO
const DEFAULT_COLOR = 'yellow';

export default function WizeToken({ children, className, color: colorProp = DEFAULT_COLOR, size }) {
  const theme = useTheme();
  const color = theme.palette[colorProp].main;

  const sides = useMemo(() => {
    const coinThickness = THICKNESS / DIAMETER;
    const edgeFaceLength = Math.PI / EDGES;
    return [...Array(EDGES)].map((_, i) => {
      return (
        <div
          key={i}
          style={{
            transform: [
              `translateY(${1 / 2 - edgeFaceLength / 2}em)`,
              `translateX(${1 / 2 - coinThickness / 2}em)`,
              `rotateZ(${(360 / EDGES) * i + 90}deg) translateX(${1 / 2}em) rotateY(90deg)`,
            ].join(' '),
            background: darken(color, ((i - EDGES / 2) ** 2 / (EDGES ** 2 / 4)) * 0.7),
          }}
        />
      );
    });
  }, [color]);

  return (
    <div
      className={className}
      css={[rootCss, ({ palette }) => ({ '.WT-coinFace': { backgroundColor: palette[colorProp].main } })]}
      style={{ fontSize: size }}
    >
      <div className={classNames('WT-coinFace', 'WT-coinFace_front')}>
        <TokenLogo />
      </div>
      <div className="WT-coinEdge">{sides}</div>
      <div className={classNames('WT-coinFace', 'WT-coinFace_back')}>
        {children !== undefined ?
          <span className="WT-coinFaceIcon">{children}</span>
        : <FontAwesomeIcon className="WT-coinFaceIcon" icon={faStar} />}
      </div>
      <div className="WT-coinShadow" />
    </div>
  );
}
